// client/src/contexts/AuthContext.js
// ============================================
// AUTH CONTEXT - FIXED VERSION
// ✅ FIX: Registration checks requiresApproval flag (no auto-login)
// ✅ FIX: Login extracts accountStatus from 403 for redirect
// ============================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, wholesalerAPI, setUnauthorizedHandler } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch team context for wholesaler users
  const fetchTeamContext = async (userData) => {
    if (userData.role !== 'wholesaler') return userData;
    try {
      const response = await wholesalerAPI.getTeamContext();
      return { ...userData, teamContext: response.data.data };
    } catch {
      // Not a team member or no team context — default to null
      return { ...userData, teamContext: null };
    }
  };

  // Normalize user and role
  const loginSuccess = (userData) => {
    if (!userData.role) {
      if (userData.businessName) userData.role = 'business';
      else if (userData.wholesalerName) userData.role = 'wholesaler';
      else userData.role = 'business';
    }
    setUser(userData);
    setIsAuthenticated(true);
  };

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        loginSuccess(parsedUser);
      } catch (error) {
        console.error('Failed to parse user from localStorage', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // ✅ FIXED: Login with proper 403 handling for pending/rejected
  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user: userData } = response.data;

      if (token && userData) {
        localStorage.setItem('token', token);
        // Fetch team context for wholesaler users before storing
        const enrichedUser = await fetchTeamContext(userData);
        localStorage.setItem('user', JSON.stringify(enrichedUser));
        loginSuccess(enrichedUser);
        return { success: true, user: enrichedUser };
      }
      return { success: false, error: 'Invalid login response' };
    } catch (error) {
      console.error('Login error:', error.response?.data?.message || error.message);
      
      const responseData = error.response?.data;
      const status = error.response?.status;
      
      // ✅ Handle 403 with account status (pending/rejected/suspended)
      if (status === 403 && responseData?.accountStatus) {
        return {
          success: false,
          error: responseData.message || 'Account not approved',
          accountStatus: responseData.accountStatus,
          rejectionReason: responseData.rejectionReason,
          companyName: responseData.companyName,
          userType: responseData.userType
        };
      }
      
      return { 
        success: false, 
        error: responseData?.message || error.message || 'Login failed' 
      };
    }
  };

  // For Wholesaler login (same logic)
  const loginWholesaler = async (credentials) => login(credentials);

  // ✅ FIXED: Register - check for requiresApproval, don't auto-login
  const registerBusiness = async (userData) => {
    try {
      const response = await authAPI.registerBusiness(userData);
      const data = response.data;

      // ✅ Check if approval is required (no token returned)
      if (data.requiresApproval || data.accountStatus === 'pending') {
        // Registration successful but needs approval - don't login
        return { 
          success: true, 
          requiresApproval: true,
          message: data.message,
          accountStatus: 'pending',
          companyName: data.companyName || data.user?.businessName,
          userType: 'business'
        };
      }

      // Legacy: If token provided (shouldn't happen with approval flow)
      const { token, user: newUser } = data;
      if (token && newUser) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(newUser));
        loginSuccess(newUser);
        return { success: true, user: newUser };
      }

      // Default: assume approval required
      return { 
        success: true, 
        requiresApproval: true,
        message: data.message || 'Registration successful!'
      };
    } catch (error) {
      console.error('Register business error:', error.response?.data?.message || error.message);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  // ✅ FIXED: Register Wholesaler - check for requiresApproval
  const registerWholesaler = async (userData) => {
    try {
      const response = await authAPI.registerWholesaler(userData);
      const data = response.data;

      // ✅ Check if approval is required (no token returned)
      if (data.requiresApproval || data.accountStatus === 'pending') {
        return { 
          success: true, 
          requiresApproval: true,
          message: data.message,
          accountStatus: 'pending',
          companyName: data.companyName || data.user?.companyName,
          userType: 'wholesaler'
        };
      }

      // Legacy: If token provided
      const { token, user: newUser } = data;
      if (token && newUser) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(newUser));
        loginSuccess(newUser);
        return { success: true, user: newUser };
      }

      return { 
        success: true, 
        requiresApproval: true,
        message: data.message || 'Registration successful!'
      };
    } catch (error) {
      console.error('Register wholesaler error:', error.response?.data?.message || error.message);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Refresh user profile (e.g. after completing first-login onboarding)
  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      const fresh = response.data?.user || response.data;
      if (!fresh) return null;
      const enriched = await fetchTeamContext(fresh);
      localStorage.setItem('user', JSON.stringify(enriched));
      loginSuccess(enriched);
      return enriched;
    } catch (error) {
      console.error('Refresh user failed:', error.response?.data?.message || error.message);
      return null;
    }
  };

  // Complete first-login: set new password and accept Terms
  const completeFirstLogin = async ({ newPassword, acceptTerms, termsVersion }) => {
    try {
      const response = await authAPI.completeFirstLogin({ newPassword, acceptTerms, termsVersion });
      const updated = response.data?.user;
      if (updated) {
        const enriched = await fetchTeamContext(updated);
        localStorage.setItem('user', JSON.stringify(enriched));
        loginSuccess(enriched);
        return { success: true, user: enriched };
      }
      return { success: true };
    } catch (error) {
      console.error('Complete first-login error:', error.response?.data?.message || error.message);
      return {
        success: false,
        error: error.response?.data?.message || 'Unable to complete first login'
      };
    }
  };

  // Set global unauthorized handler for API
  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        loginWholesaler,
        registerBusiness,
        registerWholesaler,
        logout,
        refreshUser,
        completeFirstLogin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook for consuming the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};